import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { PAYMENT_PROVIDER_ENUM } from "@antipartido-inc/contracts-ifaces"
import { Subscription } from "../../domain/subscription/Models/Subscription"
import { SubscriptionPlan } from "../../domain/subscription/Models/SubscriptionPlan"

interface SubscriptionState {
  subscription: Subscription | null
  subscriptionPlan: SubscriptionPlan | null
  isLoading: boolean
  isCancelling: boolean
  isCreating: boolean
  error?: string
}

interface SubscriptionActions {
  loadSubscription: () => Promise<Subscription | null>
  loadSubscriptionPlans: () => Promise<SubscriptionPlan | null>
  loadAll: () => Promise<void>
  cancelSubscription: () => Promise<void>
  createSubscription: (params: {
    planId: string
    provider: PAYMENT_PROVIDER_ENUM
    returnUrl: string
    cancelUrl: string
  }) => Promise<string>
  setError: (error: string | undefined) => void
  reset: () => void
}

type SubscriptionStore = SubscriptionState & SubscriptionActions

export const useSubscriptionStore = create<SubscriptionStore>()(
  devtools(
    (set, get) => ({
      subscription: null,
      subscriptionPlan: null,
      isLoading: false,
      isCancelling: false,
      isCreating: false,
      error: undefined,

      loadSubscription: async () => {
        try {
          const { subscription } = await window.domain.GetMySubscriptionUseCase.execute()
          set({ subscription })
          return subscription
        } catch (error) {
          set({ error: (error as Error).message })
          throw error
        }
      },

      loadSubscriptionPlans: async () => {
        try {
          const { plan } = await window.domain.GetSubscriptionPlansUseCase.execute()
          set({ subscriptionPlan: plan })
          return plan
        } catch (error) {
          set({ error: (error as Error).message })
          throw error
        }
      },

      loadAll: async () => {
        const state = get()
        if (state.isLoading) return
        if (state.subscription !== null && state.subscriptionPlan !== null) return

        set({ isLoading: true, error: undefined })

        try {
          await Promise.all([state.loadSubscription(), state.loadSubscriptionPlans()])
        } catch (error) {
          set({ error: (error as Error).message })
        } finally {
          set({ isLoading: false })
        }
      },

      cancelSubscription: async () => {
        set({ isCancelling: true, error: undefined })

        try {
          await window.domain.CancelMySubscriptionUseCase.execute({})
          await get().loadSubscription()
        } catch (error) {
          set({ error: (error as Error).message })
          throw error
        } finally {
          set({ isCancelling: false })
        }
      },

      createSubscription: async ({ planId, provider, returnUrl, cancelUrl }) => {
        set({ isCreating: true, error: undefined })

        try {
          const { approvalUrl } = await window.domain.CreateSubscriptionUseCase.execute({
            planId,
            provider,
            returnUrl,
            cancelUrl,
          })
          return approvalUrl
        } catch (error) {
          set({ error: (error as Error).message, isCreating: false })
          throw error
        }
      },

      setError: (error) => {
        set({ error })
      },

      reset: () => {
        set({
          subscription: null,
          subscriptionPlan: null,
          isLoading: false,
          isCancelling: false,
          isCreating: false,
          error: undefined,
        })
      },
    }),
    { name: "SubscriptionStore" },
  ),
)
